<script lang="ts" setup>
import { onMounted, ref, watch } from 'vue';

import { AppButton } from '@libs/ui';
import type {
  AutoCompleteSuggestion,
  IngredientId,
} from '@libs/trip-planner-interfaces';
import { exhaustiveGuard } from '@libs/utils';

import { useStatsigEventsStore } from '../../core/stores';

import EditTripSegments from './components/EditTripSegments.vue';
import SelectDestinationStep from './components/SelectDestinationStep.vue';
import type { EditTripSegment } from './models/editTripSegment';
import type { SelectDestinationEmit } from './models/selectDestination';
import { SelectDestinationEmitType } from './models/selectDestination';
import { EditTripStepType } from './models/editTripSteps';
import type { EditTripStep } from './models/editTripSteps';

interface EditTripViewProps {
  segments: EditTripSegment[];
  isDesktop: boolean;
  initialStep: EditTripStep;
}
const props = defineProps<EditTripViewProps>();

interface EditTripViewEmits {
  (eventName: 'add-destination', suggestion: AutoCompleteSuggestion): void;
  (
    eventName: 'add-destination-to-own-stay',
    payload: {
      suggestion: AutoCompleteSuggestion;
      ingredientId: IngredientId;
    }
  ): void;
  (
    eventName: 'change-destination',
    payload: { suggestion: AutoCompleteSuggestion; ingredientId: IngredientId }
  ): void;
  (eventName: 'change-trip-order', segments: EditTripSegment[]): void;
  (eventName: 'change-number-of-nights', segment: EditTripSegment): void;
  (eventName: 'remove-destination', segment: EditTripSegment): void;
  (eventName: 'change-step', step: EditTripStep): void;
  (eventName: 'close'): void;
}
const emit = defineEmits<EditTripViewEmits>();

const statsigEventsStore = useStatsigEventsStore();

const step = ref<EditTripStep>(props.initialStep);

function selectDestination(payload: SelectDestinationEmit): void {
  switch (payload.type) {
    case SelectDestinationEmitType.AddDestination:
      statsigEventsStore.logTripOverviewEditTripModalDestinationAdded();
      emit('add-destination', payload.suggestion);
      break;

    case SelectDestinationEmitType.AddDestinationToOwnStay:
      emit('add-destination-to-own-stay', {
        suggestion: payload.suggestion,
        ingredientId: payload.ingredientId,
      });
      break;

    case SelectDestinationEmitType.UpdateDestination:
      statsigEventsStore.logTripOverviewEditTripModalDestinationChanged();
      emit('change-destination', {
        suggestion: payload.suggestion,
        ingredientId: payload.ingredientId,
      });
      break;

    default:
      exhaustiveGuard(payload, 'Invalid select destination payload');
  }

  if (step.value.isInitialStep) {
    emit('close');
  } else {
    openOverviewStep();
  }
}

function changeTripOrder(segments: EditTripSegment[]): void {
  statsigEventsStore.logTripOverviewEditTripModalDestinationRearranged();
  emit('change-trip-order', segments);
}

function changeNumberOfNights(segment: EditTripSegment): void {
  emit('change-number-of-nights', segment);
}

function removeDestination(segment: EditTripSegment): void {
  emit('remove-destination', segment);

  if (props.segments.length === 1) {
    emit('close');
  }
}

function openOverviewStep(): void {
  step.value = {
    stepType: EditTripStepType.Overview,
  };
}

function openAddDestinationStep(stepContext?: {
  isInitialStep: boolean;
}): void {
  step.value = {
    stepType: EditTripStepType.AddDestination,
    isInitialStep: !!stepContext?.isInitialStep,
  };
}

function openUpdateDestinationStep(
  segment: EditTripSegment,
  stepContext?: { isInitialStep: boolean }
): void {
  step.value = {
    stepType: EditTripStepType.UpdateDestination,
    currentEditingSegment: segment,
    isInitialStep: !!stepContext?.isInitialStep,
  };
}

function openAddDestinationToOwnStayStep(
  ownStayDestinationIngredeintId: IngredientId,
  stepContext?: { isInitialStep: boolean }
): void {
  step.value = {
    stepType: EditTripStepType.AddDestinationToOwnStay,
    ownStayDestinationIngredeintId,
    isInitialStep: !!stepContext?.isInitialStep,
  };
}

watch(step, (currentStep) => emit('change-step', currentStep));

watch(
  () => props.initialStep,
  (initialStep) => (step.value = initialStep)
);

onMounted(() => {
  switch (props.initialStep.stepType) {
    case EditTripStepType.AddDestination:
      openAddDestinationStep({ isInitialStep: true });
      break;

    case EditTripStepType.AddDestinationToOwnStay:
      openAddDestinationToOwnStayStep(
        props.initialStep.ownStayDestinationIngredeintId,
        {
          isInitialStep: true,
        }
      );
      break;

    case EditTripStepType.UpdateDestination:
      openUpdateDestinationStep(props.initialStep.currentEditingSegment, {
        isInitialStep: true,
      });
      break;

    case EditTripStepType.Overview:
      break;

    default:
      exhaustiveGuard(props.initialStep, 'Invalid initial step');
  }
});
</script>

<template>
  <SelectDestinationStep
    v-if="
      step.stepType === EditTripStepType.AddDestination ||
      step.stepType === EditTripStepType.UpdateDestination ||
      step.stepType === EditTripStepType.AddDestinationToOwnStay
    "
    :step="step"
    @select-destination="selectDestination"
  />

  <template v-else>
    <EditTripSegments
      :segments="props.segments"
      @change-trip-order="changeTripOrder"
      @change-number-of-nights="changeNumberOfNights"
      @change-destination="openUpdateDestinationStep"
      @remove-destination="removeDestination"
    />
    <AppButton
      class="add-destination-button"
      type="secondary"
      state="enabled"
      size="small"
      :icon="{ name: 'plus' }"
      @click="openAddDestinationStep()"
    >
      {{ $t('planner.add_destination') }}
    </AppButton>
  </template>
</template>

<style lang="scss" scoped>
.message-box {
  margin-bottom: var(--spacing-24);
  text-align: left;
  justify-content: flex-start;
}
.add-destination-button {
  margin-left: 36px;
  margin-bottom: var(--spacing-24);
}
</style>
